import Cookies from "js-cookie";
import Swal from "sweetalert2";

export const CLIENT_MEDIA_REFERENCE = "Client";
export const EMPLOYEE_MEDIA_REFERENCE = "Employee";
export const EMPLOYEE_PUBLIC_MEDIA_REFERENCE = "EmployeePublic";

export const formatFieldName = (str) => {
  if (!str) return "";
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
};

export const formatDate = (dateString) => {
  const date = dateString ? new Date(dateString) : new Date();
  const options = { weekday: "long", day: "numeric", month: "long" };
  return date.toLocaleDateString("en-US", options);
};

export const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getAddressFromCoords = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
    );
    const data = await response.json();
    return data.display_name || "";
  } catch (error) {
    console.error("Error getting address:", error);
    return "";
  }
};

// =============================
// Format date as YYYY-MM-DD
// =============================
export const formatDateOnly = (date) => {
  if (!date) return "";

  const d = new Date(date);
  if (isNaN(d)) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// =============================
// Format time as 12-hour (hh:mm AM/PM)
// =============================
export const formatTime12Hour = (date) => {
  if (!date) return "";

  const d = new Date(date);
  if (isNaN(d)) return "";

  let hours = d.getHours();
  let minutes = d.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12; // Convert to 12-hour format
  minutes = String(minutes).padStart(2, "0");

  return `${hours}:${minutes} ${ampm}`;
};

export const getMediaBase64 = async (url) => {
  if (!url) return null;

  try {
    const token = Cookies.get("clientToken");
    const accountToken = Cookies.get("webapitoken");
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        webapitoken: accountToken,
      },
    });

    if (!response.ok) throw new Error("Failed to fetch media");

    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    return null;
  }
};

export const getEmployeePublicMediaBase64 = async (fileName, employeeID) => {
  if (!fileName || !employeeID) return null;

  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const DOMAIN_URL = process.env.REACT_APP_FE_BASE_URL;

  const url = `${API_URL}/assets/${DOMAIN_URL}/${EMPLOYEE_MEDIA_REFERENCE}/public/${employeeID}/${fileName}`;
  try {
    const token = Cookies.get("clientToken");
    const accountToken = Cookies.get("webapitoken");
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        webapitoken: accountToken,
      },
    });

    if (!response.ok) throw new Error("Failed to fetch media");

    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    return null;
  }
};

export const getDateMonthsAgo = (months) => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date;
};

export const getProfilePicture = async (profileMedia) => {
  if (profileMedia && profileMedia.MediaUrl) {
    return await getMediaBase64(profileMedia.MediaUrl);
  } else {
    return "";
  }
};

export const computeDays = (start, end, option = "whole") => {
  if (!start || !end) return 0;

  const s = new Date(start);
  const e = new Date(end);

  let diff = (e - s) / (1000 * 60 * 60 * 24) + 1;

  if (option === "half") diff = 0.5;

  return diff;
};

export const combineDateWithTime = (dateString, timeString) => {
  const date = new Date(dateString);
  const [hours, minutes] = timeString.split(":");
  date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
  return date;
};

export const confirmDeletePopup = async (options = {}) => {
  const {
    title = "Are you sure?",
    text = "You won't be able to revert this!",
    confirmButtonText = "Yes, delete it!",
    cancelButtonText = "Cancel",
    icon = "warning",
    confirmButtonColor = "#3085d6",
    cancelButtonColor = "#d33",
  } = options;

  const result = await Swal.fire({
    title,
    text,
    icon,
    showCancelButton: true,
    confirmButtonColor,
    cancelButtonColor,
    confirmButtonText,
    cancelButtonText,
  });

  return result.isConfirmed;
};

export const confirmActionPopup = async (options = {}) => {
  const {
    title = "Do you want to save the changes?",
    confirmButtonText = "Save",
    cancelButtonText = "Cancel",
    denyButtonText = `Don't save`,
    confirmButtonColor = "#3085d6",
    cancelButtonColor = "#d33",
  } = options;
  const result = await Swal.fire({
    title,
    showCancelButton: true,
    confirmButtonColor,
    cancelButtonColor,
    confirmButtonText,
    cancelButtonText,
    denyButtonText,
  });
};

export const successPopup = async ({ text = "" }) => {
  Swal.fire({
    title: "Deleted!",
    text: text,
    icon: "success",
  });
};

export const successActionPopup = async ({ title = "", text = "" }) => {
  Swal.fire({
    title: title,
    text: text,
    icon: "success",
  });
};

export const openAllowanceTypeModal = async ({
  id = "",
  code = "",
  description = "",
  deminimis = false,
}) => {
  const { value } = await Swal.fire({
    title: "Allowance Type",
    html: `
      <div class="swal-form">
        <div>
          <label class="swal-label">Code</label>
          <input
            id="code"
            class="swal-input"
            placeholder="DT-001"
            value="${code}"
          />
        </div>

        <div>
          <label class="swal-label">Description</label>
          <textarea
            id="description"
            class="swal-textarea"
            rows="5"
            placeholder="Optional description"
          >${description}</textarea>
        </div>

        <div class="swal-checkbox">
          <input
            type="checkbox"
            id="deminimis"
            ${deminimis ? "checked" : ""}
          />
          <label for="deminimis">De minimis</label>
        </div>
      </div>
    `,
    customClass: {
      popup: "swal-minimal",
    },
    showCancelButton: true,
    confirmButtonText: "Save",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#111827",
    cancelButtonColor: "#9ca3af",
    focusConfirm: false,
    preConfirm: () => {
      const code = document.getElementById("code").value.trim();
      const description = document.getElementById("description").value;
      const deminimis = document.getElementById("deminimis").checked;

      if (!code) {
        Swal.showValidationMessage("Code is required");
        return false;
      }

      return { id, code, description, deminimis };
    },
  });

  return value ?? null;
};

export const openDeductionTypeModal = async ({
  id = "",
  code = "",
  description = "",
  statutory = false,
}) => {
  const { value } = await Swal.fire({
    title: "Deduction Type",
    html: `
      <div class="swal-form">
        <div>
          <label class="swal-label">Code</label>
          <input
            id="code"
            class="swal-input"
            placeholder="DT-001"
            value="${code}"
          />
        </div>

        <div>
          <label class="swal-label">Description</label>
          <textarea
            id="description"
            class="swal-textarea"
            rows="5"
            placeholder="Optional description"
          >${description}</textarea>
        </div>

        <div class="swal-checkbox">
          <input
            type="checkbox"
            id="statutory"
            ${statutory ? "checked" : ""}
          />
          <label for="statutory">Statutory</label>
        </div>
      </div>
    `,
    customClass: {
      popup: "swal-minimal",
    },
    showCancelButton: true,
    confirmButtonText: "Save",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#111827",
    cancelButtonColor: "#9ca3af",
    focusConfirm: false,
    preConfirm: () => {
      const code = document.getElementById("code").value.trim();
      const description = document.getElementById("description").value;
      const statutory = document.getElementById("statutory").checked;

      if (!code) {
        Swal.showValidationMessage("Code is required");
        return false;
      }

      return { id, code, description, statutory };
    },
  });

  return value ?? null;
};

export const openDepartmentModal = async ({
  id = "",
  name = "",
  status = true,
}) => {
  const { value } = await Swal.fire({
    title: "Department",
    html: `
      <div class="swal-form">
        <div>
          <label class="swal-label">Department Name</label>
          <input
            id="department"
            class="swal-input"
            placeholder="My Department"
            value="${name}"
          />
        </div>
      </div>
    `,
    customClass: {
      popup: "swal-minimal",
    },
    showCancelButton: true,
    confirmButtonText: "Save",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#111827",
    cancelButtonColor: "#9ca3af",
    focusConfirm: false,
    preConfirm: () => {
      const department = document.getElementById("department").value.trim();

      if (!department) {
        Swal.showValidationMessage("Department Name is required");
        return false;
      }

      return { id, department };
    },
  });

  return value ?? null;
};
